import { useState } from "react";
import { Compass, Pencil, Save, X, Loader2 } from "lucide-react";
import { updateStrategyProfile } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "nicchia", label: "Nicchia" },
  { key: "tesi", label: "Tesi" },
  { key: "pain_broad", label: "Pain broad" },
  { key: "nemico", label: "Nemico" },
  { key: "format_chiave", label: "Format chiave" },
];

export function IdentityCard({ profile, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  if (!profile) return null;

  const start = () => {
    setForm({ ...FIELDS.reduce((a, f) => ({ ...a, [f.key]: profile[f.key] || "" }), {}), tesi_breve: profile.tesi_breve || "", nemici_ricorrenti: (profile.nemici_ricorrenti || []).join("\n") });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const p = await updateStrategyProfile({ ...form, nemici_ricorrenti: form.nemici_ricorrenti.split("\n").map((s) => s.trim()).filter(Boolean) });
      onSaved(p);
      setEditing(false);
      toast.success("Carta d'identità aggiornata");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-[#262626] rounded-xl bg-[#111111] p-6 space-y-5" data-testid="strategy-identity-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2"><Compass size={18} style={{ color: "#D97706" }} /> Carta d'identità del brand</h2>
          <p className="text-xs text-neutral-500 mt-1">La sequenza di Mik: tesi → pain broad → nemico → format → 5 controlli → ponte DM. Il resto è ripetizione disciplinata.</p>
        </div>
        {!editing ? (
          <button onClick={start} data-testid="strategy-identity-edit" className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white border border-[#262626] hover:border-[#404040] rounded-md px-3 py-1.5 transition-colors"><Pencil size={13} /> Modifica</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} data-testid="strategy-identity-cancel" className="flex items-center gap-1 text-xs text-neutral-400 hover:text-white border border-[#262626] rounded-md px-3 py-1.5"><X size={13} /> Annulla</button>
            <button onClick={save} disabled={saving} data-testid="strategy-identity-save" className="flex items-center gap-1 text-xs font-semibold text-black rounded-md px-3 py-1.5" style={{ background: "#D97706" }}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salva</button>
          </div>
        )}
      </div>

      <blockquote className="rounded-xl p-5 border" style={{ background: "rgba(217,119,6,0.10)", borderColor: "rgba(217,119,6,0.35)" }} data-testid="strategy-tesi-breve">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-400 mb-2">La tesi in una riga · bio · reel fissati</p>
        {editing ? (
          <textarea value={form.tesi_breve} onChange={(e) => setForm({ ...form, tesi_breve: e.target.value })} rows={2} data-testid="strategy-input-tesi_breve" className="w-full bg-black/40 border border-[#262626] rounded-md p-2 text-white text-sm" />
        ) : (
          <p className="font-display font-bold text-white text-xl leading-snug">«{profile.tesi_breve}»</p>
        )}
      </blockquote>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className={`rounded-lg border border-[#262626] p-4 ${f.key === "tesi" ? "md:col-span-2" : ""}`} data-testid={`strategy-field-${f.key}`}>
            <p className="text-[0.65rem] uppercase tracking-[0.2em] mb-2" style={{ color: "#D97706" }}>{f.label}</p>
            {editing ? (
              <textarea value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3} data-testid={`strategy-input-${f.key}`} className="w-full bg-black/40 border border-[#262626] rounded-md p-2 text-white text-sm" />
            ) : (
              <p className="text-sm text-neutral-200 leading-relaxed">{profile[f.key]}</p>
            )}
          </div>
        ))}
        <div className="rounded-lg border border-[#262626] p-4 md:col-span-2" data-testid="strategy-field-nemici">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] mb-2" style={{ color: "#D97706" }}>I 4 nemici ricorrenti (producono fatti ogni settimana)</p>
          {editing ? (
            <textarea value={form.nemici_ricorrenti} onChange={(e) => setForm({ ...form, nemici_ricorrenti: e.target.value })} rows={4} data-testid="strategy-input-nemici" className="w-full bg-black/40 border border-[#262626] rounded-md p-2 text-white text-sm" placeholder="Uno per riga" />
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(profile.nemici_ricorrenti || []).map((n, i) => <li key={i} className="text-sm text-neutral-300 flex gap-2"><span style={{ color: "#D97706" }}>▸</span>{n}</li>)}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
